import { Text } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';
import { mw, MW_CODE_STYLE, MW_MACHINE_FONT } from '@/shared/mobile/tokens';

/*
 * UN CODICE A SCHERMO, IN UN POSTO SOLO.
 *
 * ## Perche' esiste
 *
 * La stessa pastiglia era **ridichiarata in cinque punti**, con quattro nomi diversi e due
 * significati incompatibili:
 *
 * · `Token` in `content/UserGuide.tsx`, `C` in `content/DeveloperGuide.tsx` e ancora `Token` in
 *   `admin.send-communications.tsx` — tre copie quasi identiche, tutte tre col commento
 *   «MaeDesign vieta il monospace», tutte tre giuste;
 * · `CodeChip` in `qa/QaBits.tsx`, che invece passava a `ui-monospace`;
 * · `CliField({ mono })` in `clients/ClientBits.tsx`, dove `mono` **non cambiava la famiglia**
 *   e applicava `tabular-nums`, cioe' faceva la cosa giusta col nome sbagliato.
 *
 * Piu' **86 dichiarazioni inline** sparse in 60 file, in nove varianti di stack
 * (`'ui-monospace, monospace'`, `"monospace"`, `'ui-monospace, SFMono-Regular, Menlo, monospace'`…).
 * Nessuna di quelle nove era una scelta: erano nove modi di dire «qui va un codice» in assenza
 * di un modo solo. E' il buco nella tavolozza — chi non trova il token giusto prende il vicino.
 *
 * ## Le due forme, e come si scelgono
 *
 * `<Codice>` per un identificativo **su una riga**: una matricola, una targa, un id, un header,
 * un percorso, un segnaposto di template. Rende la pastiglia azzurra tenue con le cifre a
 * larghezza fissa, che e' la risposta di MaeDesign §2 (una sola famiglia; l'allineamento lo
 * fanno `tabular-nums` e il peso).
 *
 * `<TestoMacchina>` per un **blocco reso alla lettera**: un payload, un estratto di log, l'HTML
 * di un documento. Li' il monospazio allinea colonne su piu' righe, dove a variare non sono le
 * cifre ma le lettere, e `tabular-nums` non ci arriva. ⚠️ Usa il mono **di sistema**: nessun
 * font viene spedito, quindi non e' una seconda famiglia nel prodotto.
 *
 * Sono otto punti in tutta l'applicazione. Se stai per usare `<TestoMacchina>` per qualcosa
 * che sta su una riga, quello che ti serve e' `<Codice>`.
 *
 * ## Perche' `<code>` e perche' `fontFamily: 'inherit'`
 *
 * L'elemento e' `<code>`, che e' quello semantico per un frammento di codice o un
 * identificativo: lo cercano le tecnologie assistive, e lo cercava anche
 * `tests/unit/templateDetailRender.test.tsx`, che filtrava per `tagName === 'CODE'` — con uno
 * `<span>` quel test passava da verde a rosso, ed era il test ad avere ragione.
 *
 * ⚠️ Il foglio di stile del **browser** da' a `<code>` `font-family: monospace` — il generico,
 * cioe' una faccia diversa su ogni macchina. Fino al 24-09-2026 qui c'era `fontFamily: 'inherit'`
 * per impedirglielo, perche' MaeDesign §2 voleva una famiglia sola e il mono era uscito dal
 * prodotto. **Ora il mono c'e'**, e' IBM Plex Mono, ed e' proprio questo il suo perimetro
 * (v3 §4.1): la famiglia arriva da `MW_CODE_STYLE`, che la dichiara per tutti e 170 i punti.
 *
 * ⚠️ Il pericolo non e' sparito, e' cambiato di segno: se qualcuno togliesse la famiglia da
 * `MW_CODE_STYLE`, `<code>` non tornerebbe al testo normale — cadrebbe sul **generico** del
 * foglio del browser, che e' il difetto di prima con un altro nome. Lo sorveglia
 * `tests/unit/codiceNonEraMonospace.test.ts`, che gira in jsdom apposta: happy-dom il foglio
 * dello user agent non lo applica, e quella prova sarebbe verde anche col difetto dentro.
 */

interface CodeProps {
  children: ReactNode;
  /** Senza pastiglia: solo le cifre a larghezza fissa, per una cella di tabella o una riga densa. */
  bare?: boolean;
  /** Tono neutro invece dell'azzurro, quando la pastiglia sta dentro a un'altra pastiglia colorata. */
  tone?: 'accento' | 'neutro';
  /** Un gradino della scala — chiave del tema (`"xs"`…) o `var(--fs-*)`. Mai un numero: il
      default stava a 12,5px, cioe' sotto il pavimento di §4.2. */
  fz?: string;
}

/** Un identificativo in linea: matricola, targa, id, header, percorso, segnaposto. */
export function Code({ children, bare, tone = 'accento', fz = 'var(--fs-compact)' }: CodeProps) {
  if (bare) {
    return (
      <Text component="code" style={{ fontSize: fz, fontWeight: 600, ...MW_CODE_STYLE }}>
        {children}
      </Text>
    );
  }
  const color =
    tone === 'neutro'
      ? { color: mw.ink900, background: mw.surface2 }
      : { color: mw.accentText, background: mw.accentTint };
  return (
    <Text
      component="code"
      style={{
        fontSize: fz,
        fontWeight: 600,
        borderRadius: 6,
        padding: '1px 6px',
        whiteSpace: 'nowrap',
        ...color,
        ...MW_CODE_STYLE,
      }}
    >
      {children}
    </Text>
  );
}

/**
 * Un blocco di testo macchina reso alla lettera. Perimetro dichiarato: vedi `MW_FONT_MACCHINA`.
 *
 * ⚠️ `overflowX: 'auto'` non e' un dettaglio: un payload o una riga di log non vanno a capo, e
 * senza questo allargano il contenitore che li ospita invece di scorrere dentro di se'.
 */
export function MachineText({
  children,
  fz = 'var(--fs-compact)',
  style,
}: {
  children: ReactNode;
  /** Come sopra: un gradino, non un numero. */
  fz?: string;
  /** Vincoli propri del blocco: `maxHeight` con `overflow`, larghezza piena. */
  style?: CSSProperties;
}) {
  return (
    <Text
      component="pre"
      style={{
        margin: 0,
        fontFamily: MW_MACHINE_FONT,
        fontSize: fz,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowX: 'auto',
        color: mw.ink900,
        background: mw.surface2,
        border: `1px solid ${mw.line}`,
        borderRadius: 8,
        padding: '10px 12px',
        ...style,
      }}
    >
      {children}
    </Text>
  );
}
